import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  RegisterOptions,
  UseFormTrigger,
} from "react-hook-form";
import { Controller } from "react-hook-form";

import type { OptionType } from "./Select";
import Select from "./Select";

type ControlledSelectProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string | null;
  errors: FieldErrors<T>;
  onSave: (value: OptionType) => Promise<void>;
  readOnly?: boolean;
  maxLength?: number;
  classNames?: string;
  options: OptionType[];
  placeholder?: string;
  preventClear?: boolean;
  rules?: RegisterOptions<T, Path<T>>;
  tooltip?: string;
  tooltipPlace?: "top" | "right" | "bottom" | "left";
  widthVariant?: "medium" | "wide" | "full";
  trigger?: UseFormTrigger<T>;
};

const ControlledSelect = <T extends FieldValues>({
  name,
  label,
  errors,
  control,
  options = [],
  placeholder,
  onSave,
  readOnly,
  rules,
  preventClear = false,
  tooltip,
  tooltipPlace,
  widthVariant,
  trigger,
}: ControlledSelectProps<T>) => {
  return (
    <Controller
      rules={rules}
      control={control}
      name={name}
      render={({ field: { value, name, onChange } }) => {
        const selectedOption =
          options && Array.isArray(options)
            ? options.find((o) => o && o.value === value)
            : undefined;

        return (
          <Select
            name={name}
            placeholder={placeholder}
            label={label}
            readOnly={readOnly}
            value={selectedOption}
            options={options || []}
            isClearable={!preventClear}
            backspaceRemovesValue={!preventClear}
            onChange={(option: OptionType) => {
              if (option || !preventClear) {
                onChange(option);
                onSave(option);
                setTimeout(() => trigger?.(name), 0);
              }
            }}
            errors={errors}
            tooltip={tooltip}
            tooltipPlace={tooltipPlace}
            widthVariant={widthVariant}
          />
        );
      }}
    />
  );
};

export default ControlledSelect;
